import {useState, useRef, useEffect} from 'react';
import {useIntl} from 'react-intl';
import cleanFieldValue from 'utils/cleanFieldValue';
import {ReactComponent as Housing} from 'assets/icons/housing.svg';
import {ReactComponent as Award} from 'assets/icons/award.svg';
import {ReactComponent as Message} from 'assets/icons/message.svg';
import {ReactComponent as Paper} from 'assets/icons/paper.svg';
import {ReactComponent as Case} from 'assets/icons/case.svg';
import {ReactComponent as Card} from 'assets/icons/credit-card.svg';
import {postFavoriteProgram} from 'Callbacks/flagFavorites';
import {ReactComponent as Heart} from 'assets/icons/heart.svg';

const QuickViewCard = ({data, url, programType, userLoggedIn, isFavorite, setFavorites}) => {
  const intl = useIntl();
  const {
    nid,
    program_image_quick_view,
    displayed_destination,
    field_short_title,
    title,
    field_prgm_options,
    field_prgm_term_type,
    field_short_desc,
    field_prgm_type,
    field_prgm_housing_opt,
    field_prgm_credits,
    field_prgm_lang_instruct,
    field_prgm_lang_pre,
    field_prgm_part_intern,
    field_prgm_visa,
    program_offers
  } = data || {};

  const modal = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const isInternship = field_prgm_type === 'Internship';

  useEffect(() => {
    // eslint-disable-next-line no-undef
    Drupal.attachBehaviors(modal.current);
  }, []);

  const glaceItems = [
    {
      label: intl.formatMessage({id: 'ProgramModal.Housing'}),
      value: cleanFieldValue(field_prgm_housing_opt, ''),
      icon: <Housing/>,
    },
    {
      label: intl.formatMessage({id: 'ProgramModal.Credits'}),
      value: cleanFieldValue(field_prgm_credits, ''),
      icon: <Award/>,
    },
    {
      label: isInternship ? intl.formatMessage({id: 'ProgramModal.PrimarySpokenLanguage'}) : intl.formatMessage({id: 'ProgramModal.LanguageOfInstruction'}),
      value: cleanFieldValue(field_prgm_lang_instruct, ''),
      icon: <Message/>,
    },
    {
      label: intl.formatMessage({id: 'ProgramModal.LanguagePrerequisites'}),
      value: cleanFieldValue(field_prgm_lang_pre, ''),
      icon: <Paper/>,
    },
    {
      label: intl.formatMessage({id: 'ProgramModal.PartTimeInternship'}),
      value: cleanFieldValue(field_prgm_part_intern, ''),
      icon: <Case/>,
    },
    {
      label: intl.formatMessage({id: 'ProgramModal.VisaRequired'}),
      textTransform: 'capitalize',
      value: cleanFieldValue(field_prgm_visa, ''),
      icon: <Card/>,
    },
  ];

  const changeTabIndex = (index) => {
    setActiveTab(index);
  };

  const programOffers = () => {
    if (!program_offers) {
      return '';
    }
    else if (typeof program_offers === 'object') {
      const offers = program_offers.map((item) => {
        return JSON.parse(item);
      });

      return (
        <div className="offers-about offers-about--quick-view flex flex-col mb-34">
          <div className="flex justify-start border-b border-grey-veryLight mb-24">
            {offers.map((offer, index) => {
              return (
                <h5
                  className={`js-offers-about-tab offers-about--quick-view__tab py-16 ${activeTab === index ? 'active' : ''}`}
                  onClick={() => changeTabIndex(index)}>
                  {offer.label}
                </h5>
              );
            })}
          </div>
          <div className="js-offers-content offers-about__content">
            {offers.map((offer, index) => {
              return (
                <div className={activeTab === index ? 'active' : ''}
                     dangerouslySetInnerHTML={{__html: offer.offer_render}}/>
              );
            })}
          </div>
        </div>
      );
    }
    else if (typeof program_offers === 'string') {
      const offers = JSON.parse(program_offers);

      return (
        <div className="offers-about offers-about--quick-view flex flex-col mb-34">
          <div className="flex justify-start border-b border-grey-veryLight mb-24">
            <h5 className="js-offers-about-tab offers-about--quick-view__tab py-16 font-bold active">{offers.label}</h5>
          </div>
          <div className="js-offers-content offers-about__content">
            <div className="active" dangerouslySetInnerHTML={{__html: offers.offer_render}}/>
          </div>
        </div>
      );
    }
  };

  const favorite = () => {
    const loggedIn = (
      <>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="#" className="flag-button" rel="nofollow" onClick={(e) => {
          e.preventDefault();
          postFavoriteProgram(intl, nid, !isFavorite).then((res) => {
            if (res !== null) {
              setFavorites(res);
            }
          });
        }}>
          <span className="visually-hidden">{intl.formatMessage({id: 'ProgramCard.Favorites.Button'})}</span>
          <Heart/>
          <span>{intl.formatMessage({id: 'ProgramCard.Favorites.Modal.Button'})}</span>
        </a>
      </>
    );

    const notLoggedIn = (
      <>
        <button className="flag-button flag-button--disabled">
          <span className="visually-hidden">{intl.formatMessage({id: 'ProgramCard.Favorites.NotLogged.Button'})}</span>
          <Heart/>
          <span>{intl.formatMessage({id: 'ProgramCard.Favorites.Modal.Button'})}</span>
        </button>
      </>
    );
    return (
      <div className="program--favorite flex justify-end pt-8 ml-auto">
        <div className={`flag flag-favorites ${isFavorite ? 'action-unflag' : 'action-flag'}`}
             style={{visibility: isFavorite === null ? 'hidden' : ''}}>
          {userLoggedIn ? loggedIn : notLoggedIn}
        </div>
      </div>
    );
  };

  return (
    <article ref={modal}
             className="node node--type-program node--view-mode-quick-view js-dL-programfinderModalCTA-program program program-quick-view flex shadow-default bg-white overflow-auto lg:h-full xl:h-auto">
      <div className="program-quick-view__image-container"
           dangerouslySetInnerHTML={{__html: program_image_quick_view}}/>

      <div
        className="program-quick-view__details-container py-56 px-36 xl:px-54 flex-col flex relative w-full inline-flag">
        <div className="program--location-wrapper flex justify-between">

          {displayed_destination ?
            <h3 className="program--location text-grey-dark mb-8">
              {typeof displayed_destination === 'string' ? displayed_destination : displayed_destination.join(' | ')}
            </h3> : ''
          }
          {favorite()}
        </div>


        <h2 className="program--title mb-16 font-roboto text-32 font-medium leading-tight lg:text-40">
          <a href={url} rel="bookmark" className="text-grey-dark no-underline link--custom-underline">
            {field_short_title ? field_short_title : title}
          </a>
        </h2>

        <div className="program-quick-view__banner flex flex-col xl:flex-row xl:items-center mb-30 gap-y-10">
          <div className="program--type mr-10 whitespace-nowrap">
            {programType}
          </div>

          {field_prgm_options ?
            <div className="program--options mr-10">
              <label className="inline text-grey-text">
                {intl.formatMessage({id: 'ProgramCard.ProgramOptions'})}
              </label>
              {typeof field_prgm_options === 'object' ? field_prgm_options.join(', ') : field_prgm_options}
            </div> : ''
          }

          {field_prgm_term_type ?
            <div className="program--length mr-10">
              <label className="text-grey-text inline">
                <span className="mr-10">&bull;</span>
                {intl.formatMessage({id: 'ProgramCard.TermType'})}
              </label>
              {typeof field_prgm_term_type === 'object' ? field_prgm_term_type.join(', ') : field_prgm_term_type}
            </div> : ''
          }
        </div>

        <div className="program-quick-view__details flex flex-row xl:justify-between">
          <div className="program-quick-view__details-right">
            {field_short_desc ?
              <div className="program-description mb-32">
                {field_short_desc}
                <a href={url} className="link--black font-bold underline block mt-16">
                  {intl.formatMessage({id: 'ProgramModal.ReadMore'})}
                </a>
              </div> : ''
            }

            {programOffers()}

            <div className="flex">
              <a className="button mr-5 js-dL-programfinderModalCTA" href={url}>
                {intl.formatMessage({id: 'ProgramModal.SeeProgramDetails.Button'})}
              </a>

              <button className="button button--secondary js-dL-programfinderModalCTA js-apply-now">
                {intl.formatMessage({id: 'ProgramModal.ApplyNow.Button'})}
              </button>
            </div>
          </div>

          <div className="program-quick-view__details-left mt-20 xl:mt-0">
            <div className="flex flex-col gap-y-20">
              {glaceItems.map((item) => {
                if (!item.value) {
                  return '';
                }

                return (
                  <div className="glance-item flex">
                    <div
                      className="glance-item__icon fill-teal-default w-32 flex items-start justify-center pt-4 mr-24">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="glance-item__heading text-16 font-karla font-bold">
                        {item.label}
                      </h4>
                      <div className="glance-item__description">
                        {
                          (() => {
                            if (item.value === 'maybe') {
                              return (
                                <p>Maybe</p>
                              )
                            } else if (item.value === 'required') {
                              return (
                                <p>Yes</p>
                              )
                            } else {
                              return (
                                <p>{item.value}</p>
                              )
                            }
                          })()
                        }
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </article>
  )
};

export default QuickViewCard;
